import {
  isHostTargetingStrategy,
  isKubernetesTargetingStrategy
} from 'components/attacks/create/targets/target-utils'
import { getTargetMultiValueMap } from 'components/attacks/utils/targets-multi-value-map'
import { cleanJoin } from 'utils/strings'

import type {
  AttackSamplingConfiguration,
  ContainerTargetingStrategy,
  EvenSampling,
  ExactSampling,
  HostTargetingStrategy,
  KubernetesTargetingStrategy,
  TargetingStrategy
} from 'types/api/attacks/details/targeting'

export function isEvenSampling(
  sampling?: AttackSamplingConfiguration
): sampling is EvenSampling {
  return sampling?.type === 'Even'
}

export function isExactSampling(
  sampling?: AttackSamplingConfiguration
): sampling is ExactSampling {
  return sampling?.type === 'Exact'
}

export function getSamplingLabel(sampling?: AttackSamplingConfiguration) {
  if (!sampling) {
    return ''
  }

  if (isEvenSampling(sampling)) {
    const percent = sampling.percent
    return percent === 100 ? '' : `${percent}% of`
  }

  const count = sampling.count
  return count ? `${count} of` : ''
}

export function getK8sLabelSelectionDisplay({
  labelSelection
}: KubernetesTargetingStrategy) {
  if (!labelSelection) return ''

  const { allNamespaces, labelSelector, namespaces } = labelSelection

  if (allNamespaces) {
    return labelSelector
  }

  const isNamespacesNonEmpty = namespaces && namespaces.length > 0
  const namespacesLabel = isNamespacesNonEmpty
    ? `(${namespaces.join(', ')})`
    : ''

  return [labelSelector, namespacesLabel].filter(Boolean).join(' ')
}

function handleKubernetesTargets(
  targetingStrategy: KubernetesTargetingStrategy,
  {
    isTargetAll,
    samplingLabel
  }: { samplingLabel: string; isTargetAll: boolean }
): string[] {
  if (isTargetAll) {
    return [
      cleanJoin(['Targeting', samplingLabel, 'all Kubernetes resources'], ' ')
    ]
  }

  if (targetingStrategy.labelSelection) {
    return [getK8sLabelSelectionDisplay(targetingStrategy)]
  }

  if (targetingStrategy.names) {
    return [
      ...targetingStrategy.names.map(
        ({ namespace, name }) => `${name} (${namespace})`
      ),
      ...(samplingLabel
        ? [`${samplingLabel} selected Kubernetes resources`]
        : [])
    ]
  }

  return []
}

function handleHostAndContainerTargets(
  targetingStrategy: ContainerTargetingStrategy | HostTargetingStrategy,
  {
    isTargetAll,
    samplingLabel
  }: { samplingLabel: string; isTargetAll: boolean }
): string[] {
  const strategyLabel = isHostTargetingStrategy(targetingStrategy)
    ? 'Hosts'
    : 'Containers'

  if (isTargetAll) {
    return [
      cleanJoin(['Targeting', samplingLabel, `all ${strategyLabel}`], ' ')
    ]
  }

  const valueMap = getTargetMultiValueMap(targetingStrategy)

  if (!valueMap) return []

  return [
    ...Object.entries(valueMap).map(
      ([key, values]) => `${key}: ${values.join(', ')}`
    ),
    ...(samplingLabel ? [`${samplingLabel} selected ${strategyLabel}`] : [])
  ]
}

/**
 * Returns the attack targets in a human-readable format
 */
export function getAttackTargetsForDisplay(
  targetingStrategy: TargetingStrategy,
  attackSamplingConfiguration?: AttackSamplingConfiguration
): string[] {
  if (targetingStrategy.ids?.length) {
    return targetingStrategy.ids
  }

  const isKubernetes = isKubernetesTargetingStrategy(targetingStrategy)
  const commonArgs = {
    isTargetAll: Boolean(targetingStrategy.isAll),
    samplingLabel: getSamplingLabel(attackSamplingConfiguration)
  }

  return isKubernetes
    ? handleKubernetesTargets(targetingStrategy, commonArgs)
    : handleHostAndContainerTargets(targetingStrategy, commonArgs)
}
